// Gesprächskarte (T48): Anrufsymbol auf der Zeitleiste, Kopf mit Titel, Lead, Richtung, Dauer und Mitarbeiter, darunter die Notiz
// (Suchbegriffe markiert, „Mehr anzeigen“) und der Player für Aufnahme bzw. Mailbox-Nachricht.
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { Gespraech } from "../api/gespraeche";
import { Avatar } from "../components/Bausteine";
import { AufnahmePlayer } from "../components/AufnahmePlayer";
import { Icon } from "../components/Icon";
import { MenueKnopf, type MenuePunkt } from "../components/Menue";
import { useMeldung } from "../components/Meldungen";
import { useAnrufen } from "../components/Telefonnummer";
import { ergebnisText } from "../aktivitaeten/texte";
import { dauerKurz } from "../aktivitaeten/zeiten";
import { kopieren } from "../werkzeuge/tasten";
import { relativ, zeitpunktLang } from "../werkzeuge/zeit";
import { ablaufText, ablaufTon, BEWERTUNG_TEXT, detailPfad, hervorheben, notizAusschnitt } from "./logik";

/** Zeigt die Karte den Ablauf der Aufnahme selbst? Der Player (7B) zeigt „läuft in n T. ab“ schon selbst; nur wieder einschalten, wenn er es nicht mehr tut. */
const ABLAUF_SELBST = false;

function Notiz({ text, begriffe, offen, onUmschalten }: { text: string; begriffe: string[]; offen: boolean; onUmschalten: () => void }) {
  const ausschnitt = useMemo(() => notizAusschnitt(text), [text]);
  const gezeigt = offen || !ausschnitt.gekuerzt ? text : ausschnitt.text + " …";
  const stuecke = useMemo(() => hervorheben(gezeigt, begriffe), [gezeigt, begriffe]);
  return (
    <p className="gesp-karte__notiz">
      {stuecke.map((s, i) =>
        s.treffer ? (
          <mark key={i} className="gesp-treffer">
            {s.text}
          </mark>
        ) : (
          <span key={i}>{s.text}</span>
        ),
      )}
      {ausschnitt.gekuerzt ? (
        <button type="button" className="gesp-karte__mehr" onClick={onUmschalten}>
          {offen ? "Weniger anzeigen" : "Mehr anzeigen"}
        </button>
      ) : null}
    </p>
  );
}

interface Props {
  gespraech: Gespraech;
  /** Suchbegriffe zum Markieren in der Notiz */
  begriffe?: string[];
  /** Notiz von Anfang an ganz zeigen (Detailseite) */
  aufgeklappt?: boolean;
  /** Mitarbeiter anzeigen (bei eigener Ansicht ohne `gespraeche_alle` überflüssig) */
  mitMitarbeiter?: boolean;
  /** Karte ohne Zeitleiste und Details-Link (Detailseite) */
  einzeln?: boolean;
}

export function GespraechKarte({ gespraech: g, begriffe = [], aufgeklappt = false, mitMitarbeiter = true, einzeln = false }: Props) {
  const navigate = useNavigate();
  const meldung = useMeldung();
  const anrufen = useAnrufen();
  const [offen, setOffen] = useState(aufgeklappt);
  const verpasst = g.ergebnis === "no-answer" || g.ergebnis === "busy" || g.ergebnis === "abandoned";
  const nummer = g.nummer_formatiert ?? g.nummer;
  const gegenueber = g.kontakt_name ?? nummer ?? "unbekannte Nummer";
  const titel = g.titel ?? (g.richtung === "ausgehend" ? `Anruf an ${gegenueber}` : `Anruf von ${gegenueber}`);
  const dauer = g.dauer_s ? dauerKurz(g.dauer_s) : "";
  const a = g.aufnahme;
  const ablauf = a.art === "aufnahme" && a.verfuegbar ? ablaufText(a.laeuft_ab_am) : "";

  const kopierenMitMeldung = (text: string, ok: string) =>
    void kopieren(text).then((gelungen) => meldung.zeige(gelungen ? "ok" : "fehler", gelungen ? ok : "Kopieren hat nicht geklappt."));
  const punkte: MenuePunkt[] = [
    { label: "Details öffnen", icon: "karte", onClick: () => navigate(detailPfad(g.call_id)) },
    ...(g.lead_id ? [{ label: "Lead öffnen", icon: "leads", onClick: () => navigate(`/lead/${encodeURIComponent(g.lead_id as string)}`) } as MenuePunkt] : []),
    {
      label: "Link zum Gespräch kopieren",
      icon: "link",
      trenner: true,
      onClick: () => kopierenMitMeldung(`${window.location.origin}${detailPfad(g.call_id)}`, "Link kopiert."),
    },
    ...(g.nummer ? [{ label: "Nummer kopieren", icon: "kopieren", onClick: () => kopierenMitMeldung(g.nummer as string, "Nummer kopiert.") } as MenuePunkt] : []),
    ...(g.notiz ? [{ label: "Notiz kopieren", icon: "notiz", onClick: () => kopierenMitMeldung(g.notiz as string, "Notiz kopiert.") } as MenuePunkt] : []),
  ];

  return (
    <article className="gesp-karte" aria-label={titel}>
      <div className={`gesp-karte__marke${verpasst ? " gesp-karte__marke--verpasst" : g.ergebnis && g.ergebnis !== "answered" ? " gesp-karte__marke--neutral" : ""}`}>
        <Icon name="telefon" groesse={14} />
      </div>
      <div className="gesp-karte__koerper">
        <div className="gesp-karte__kopf">
          <span className="gesp-karte__titel" title={titel}>
            {einzeln ? titel : <a href={detailPfad(g.call_id)} onClick={(e) => (e.metaKey || e.ctrlKey ? undefined : (e.preventDefault(), navigate(detailPfad(g.call_id))))}>{titel}</a>}
          </span>
          {g.lead_name ? (
            <span className="gesp-karte__lead" title={g.lead_name}>
              {g.lead_id ? (
                <a href={`/lead/${encodeURIComponent(g.lead_id)}`} onClick={(e) => (e.metaKey || e.ctrlKey ? undefined : (e.preventDefault(), navigate(`/lead/${encodeURIComponent(g.lead_id as string)}`)))}>
                  {g.lead_name}
                </a>
              ) : (
                g.lead_name
              )}
            </span>
          ) : nummer ? (
            <span className="gesp-karte__lead" title="Kein Lead zugeordnet">
              {nummer}
            </span>
          ) : null}
          <span className="gesp-karte__meta">
            <span className="gesp-richtung" title={g.richtung === "ausgehend" ? "Ausgehend" : "Eingehend"}>
              <Icon name={g.richtung === "ausgehend" ? "ausgehend" : "eingehend"} groesse={13} />
            </span>
            {dauer ? (
              <>
                <Icon name="uhr" groesse={13} />
                {dauer}
              </>
            ) : g.ergebnis ? (
              <span>{ergebnisText(g.ergebnis)}</span>
            ) : null}
          </span>
          {g.qualitaet ? (
            <span className={`gesp-bewertung gesp-bewertung--${g.qualitaet.bewertung}`} title={`Leitungsqualität: ${BEWERTUNG_TEXT[g.qualitaet.bewertung]}${g.qualitaet.mos !== null ? ` (MOS ${g.qualitaet.mos})` : ""}`}>
              {BEWERTUNG_TEXT[g.qualitaet.bewertung]}
            </span>
          ) : null}
          <span className="gesp-karte__rechts">
            <span className="gesp-karte__aktionen">
              {g.nummer && g.lead_id ? (
                <button
                  type="button"
                  className="symbolknopf symbolknopf--klein"
                  title={`Anrufen: ${nummer}`}
                  aria-label={`Anrufen: ${nummer}`}
                  onClick={() => void anrufen(g.nummer as string, g.lead_id as string, g.kontakt_id, { leadName: g.lead_name, kontaktName: g.kontakt_name })}
                >
                  <Icon name="telefon" groesse={14} />
                </button>
              ) : null}
              <MenueKnopf knopfKlasse="symbolknopf symbolknopf--klein" titel="Mehr" punkte={punkte} ausrichtung="end" breite={240}>
                <Icon name="mehr" groesse={14} />
              </MenueKnopf>
            </span>
            {mitMitarbeiter && g.user_name ? (
              <span title={g.user_name} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <Avatar name={g.user_name} id={g.user_id} groesse={18} />
              </span>
            ) : null}
            <time dateTime={g.zeit} title={zeitpunktLang(g.zeit)}>
              {relativ(g.zeit)}
            </time>
          </span>
        </div>
        {g.notiz ? <Notiz text={g.notiz} begriffe={begriffe} offen={offen} onUmschalten={() => setOffen((o) => !o)} /> : null}
        {a.art && (a.verfuegbar || a.grund === "abgelaufen") ? (
          <div className="gesp-karte__fuss">
            {a.verfuegbar ? (
              <>
                <AufnahmePlayer callId={g.call_id} art={a.art} dauerS={a.dauer_s ?? g.dauer_s} laeuftAbAm={a.laeuft_ab_am} />
                {ABLAUF_SELBST && ablauf ? <span className={`gesp-hinweis gesp-ablauf--${ablaufTon(a.laeuft_ab_am)}`}>{ablauf}</span> : null}
                {a.art === "voicemail" ? <span className="gesp-hinweis">Nachricht auf der Mailbox</span> : null}
              </>
            ) : (
              <span className="gesp-hinweis" title="Close löscht Aufnahmen nach 7 Tagen.">
                Aufnahme abgelaufen
              </span>
            )}
          </div>
        ) : null}
      </div>
    </article>
  );
}
